import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { A } from "./assets";
import "./tk.css";

/* ------------------------------------------------------------------ config
   Everything a developer needs to wire is in this object. */
export const TK = {
  trialUrl: "https://www.thatkorner.online/restaurant-signup",
  loginUrl: "https://www.thatkorner.online/login",
  legal: {
    privacy: "https://www.thatkorner.online/privacy",
    terms: "https://www.thatkorner.online/terms",
    cookies: "https://www.thatkorner.online/cookies",
    dpa: "https://www.thatkorner.online/dpa",
  },
  email: "contact@thatkorner.com",
  phones: ["+971 55 976 7778", "+971 56 177 4051"],
  legalEntity: "KARV Ventures LLC",
  address: "First Floor, Sharjah Media City, Al Batayih, Sharjah, UAE",
  formEndpoint: "/api/public/contact",
  social: [
    { label: "Instagram", url: "https://www.instagram.com/that_korner/", d: "M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8C2.4 3.9 3.9 2.4 7.2 2.3 8.4 2.2 8.8 2.2 12 2.2zm0 4.7a5.1 5.1 0 100 10.2 5.1 5.1 0 000-10.2zm0 8.4a3.3 3.3 0 110-6.6 3.3 3.3 0 010 6.6zm5.3-9.8a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4z" },
    { label: "Facebook", url: "https://www.facebook.com/thatkorner", d: "M22 12a10 10 0 10-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0022 12z" },
    { label: "LinkedIn", url: "https://www.linkedin.com/company/thatkorner/about/", d: "M20.4 20.5h-3.6v-5.6c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9v5.7H9.3V9h3.4v1.6h.1c.5-.9 1.6-1.8 3.4-1.8 3.6 0 4.3 2.4 4.3 5.5v6.2zM5.3 7.4a2.1 2.1 0 110-4.2 2.1 2.1 0 010 4.2zM7.1 20.5H3.6V9h3.5v11.5z" },
    { label: "X", url: "https://x.com/thatkorner", d: "M17.8 3h3.1l-6.8 7.8 8 10.2h-6.3l-4.9-6.4L5.3 21H2.2l7.3-8.3L1.8 3h6.4l4.4 5.8L17.8 3zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5z" },
  ],
  // Analytics (GA4 + Microsoft Clarity) load only after the visitor accepts analytics cookies.
  onConsent: (granted: boolean) => {
    if (!granted || typeof window === "undefined") return;
    const w = window as unknown as { __tkAnalytics?: boolean; dataLayer: unknown[]; gtag: (...a: unknown[]) => void; clarity?: unknown };
    if (w.__tkAnalytics) return;
    w.__tkAnalytics = true;
    const g = document.createElement("script");
    g.async = true;
    g.src = "https://www.googletagmanager.com/gtag/js?id=G-CHJ3PEL8L7";
    document.head.appendChild(g);
    w.dataLayer = w.dataLayer || [];
    w.gtag = function () { w.dataLayer.push(arguments); };
    w.gtag("js", new Date());
    w.gtag("config", "G-CHJ3PEL8L7");
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      const t = l.createElement(r) as HTMLScriptElement; t.async = true; t.src = "https://www.clarity.ms/tag/" + i;
      const y = l.getElementsByTagName(r)[0]; y.parentNode!.insertBefore(t, y);
    })(window, document, "clarity", "script", "xav9bzr246");
  },
};

/* ------------------------------------------------------------------ links
   In production these are ordinary paths. The standalone preview build sets
   window.__TK_PREVIEW__ and uses hash tokens instead. */
const PREVIEW = typeof window !== "undefined" && (window as unknown as { __TK_PREVIEW__?: boolean }).__TK_PREVIEW__ === true;
export function href(path: string, hash?: string): string {
  if (PREVIEW) {
    const p = path === "/" ? "home" : path.replace(/^\//, "");
    return "#" + p + (hash ? "-" + hash : "");
  }
  return path + (hash ? "#" + hash : "");
}
export const CONTACT = "#contact";

/* ------------------------------------------------------------------ icons */
const PATHS: Record<string, string> = {
  arrow: "M5 12h14M13 6l6 6-6 6",
  check: "M5 12.5l4.5 4.5L19 7.5",
  minus: "M6 12h12",
  plus: "M12 6v12M6 12h12",
  menu: "M4 7h16M4 12h16M4 17h16",
  close: "M6 6l12 12M18 6L6 18",
  send: "M4 12l16-8-6 16-3-7-7-1z",
  mic: "M12 4a3 3 0 013 3v5a3 3 0 01-6 0V7a3 3 0 013-3zM6 11a6 6 0 0012 0M12 17v3",
  chat: "M4 5h16v11H9l-5 4V5z",
  shield: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z",
  globe: "M12 3a9 9 0 100 18 9 9 0 000-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18",
  heart: "M12 20s-7-4.4-7-10a4 4 0 017-2.6A4 4 0 0119 10c0 5.6-7 10-7 10z",
  glass: "M8 3h8l-1 7a3 3 0 01-6 0L8 3zM12 13v7M8 20h8",
  chart: "M4 20V10M10 20V4M16 20v-8M22 20H2",
  scatter: "M4 4v16h16M9 14h.01M13 9h.01M17 12h.01M11 17h.01M17 6h.01",
  coins: "M12 6c4 0 7-1 7-2.5S16 1 12 1 5 2 5 3.5 8 6 12 6zM5 3.5v5C5 10 8 11 12 11s7-1 7-2.5v-5M5 8.5v5C5 15 8 16 12 16s7-1 7-2.5v-5",
  basket: "M4 9h16l-2 11H6L4 9zM9 9l3-6 3 6",
  slider: "M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M6 14v6",
  order: "M7 3h10v18l-2.5-2-2.5 2-2.5-2L7 21V3zM10 8h4M10 12h4",
  flame: "M12 3c1 4 5 5 5 10a5 5 0 01-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-1-5 1-9z",
  receipt: "M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6",
  table: "M3 9h18M5 9v10M19 9v10M7 5h10l2 4H5l2-4z",
  qr: "M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v6h-4v-2M14 18h2",
  bed: "M3 18V7M3 13h18v5M21 18v-3a4 4 0 00-4-4H9v2M6 10.5h.01",
  users: "M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM2.5 20c.5-3.5 3-5.5 6.5-5.5s6 2 6.5 5.5M16 4.5a3.5 3.5 0 010 6.5M18 14.8c2 .6 3.2 2.3 3.5 5.2",
  card: "M3 6h18v12H3zM3 10h18M7 15h3",
  printer: "M7 8V3h10v5M5 17H3V9h18v8h-2M7 14h10v7H7z",
  box: "M3 8l9-5 9 5v8l-9 5-9-5V8zM3 8l9 5 9-5M12 13v8",
  layers: "M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5",
  lock: "M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 017 0v3",
  key: "M14 10a4 4 0 10-3.6 4L12 16h2v2h2v2h3v-3l-5.4-5.4c.3-.5.4-1 .4-1.6zM8.5 9h.01",
  alert: "M12 4l9 16H3L12 4zM12 10v4M12 17h.01",
  star: "M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6L3.3 9.3l6.1-.7L12 3z",
  bell: "M6 16V11a6 6 0 0112 0v5l2 2H4l2-2zM10 20h4",
  plug: "M9 3v5M15 3v5M6 8h12v3a6 6 0 01-12 0V8zM12 17v4",
  eye: "M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 9a3 3 0 100 6 3 3 0 000-6z",
  file: "M6 3h8l4 4v14H6V3zM14 3v4h4M9 12h6M9 16h6",
  pin: "M12 21s7-6.2 7-11.5a7 7 0 10-14 0C5 14.8 12 21 12 21zM12 7a2.5 2.5 0 100 5 2.5 2.5 0 000-5z",
};
export function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={PATHS[name] ?? PATHS.arrow} />
    </svg>
  );
}

/* ------------------------------------------------------------------ small parts */
export const Eyebrow = ({ children }: { children: ReactNode }) => <p className="tk-eyebrow">{children}</p>;

export function Btn({ to, children, kind, sm, external }: { to: string; children: ReactNode; kind?: "ghost" | "ink" | "gold"; sm?: boolean; external?: boolean }) {
  const cls = ["tk-btn", kind ? `tk-btn--${kind}` : "", sm ? "tk-btn--sm" : ""].join(" ").trim();
  return (
    <a className={cls} href={to} {...(external ? { target: "_blank", rel: "noopener" } : {})}>
      {children}
      {!sm && <Icon name="arrow" />}
    </a>
  );
}
export const More = ({ to, children }: { to: string; children: ReactNode }) => (
  <a className="tk-link" href={to}>{children}<Icon name="arrow" /></a>
);

/** Every figure on the site carries one of these. Definitions are printed in the footer. */
export type BasisKind = "Pilot-observed" | "Modelled" | "By design" | "Product capability" | "Commercial term" | "Contractual" | "Illustrative";
export const Basis = ({ kind }: { kind: BasisKind }) => <span className="tk-basis">{kind}</span>;

export function Stat({ n, children, basis }: { n: string; children: ReactNode; basis: BasisKind }) {
  return (
    <div className="tk-stat">
      <span className="tk-num">{n}</span>
      <p>{children}</p>
      <Basis kind={basis} />
    </div>
  );
}

export function Item({ icon, title, children, tag }: { icon?: string; title: string; children: ReactNode; tag?: string }) {
  return (
    <div className="tk-item">
      {icon && <Icon name={icon} className="tk-item__icon" />}
      <h3>{title}{tag && <span className="tk-tag">{tag}</span>}</h3>
      <p>{children}</p>
    </div>
  );
}

/** Chef Buddy looking in from the bottom corner of a section. Decorative. */
export const Peek = ({ left }: { left?: boolean }) => (
  <div className={"tk-peek" + (left ? " tk-peek--left" : "")} aria-hidden="true"><img src={A.chefBuddy} alt="" loading="lazy" /></div>
);

export function Buddy({ width, float, children, bubble = "left", style }: { width: number | string; float?: boolean; children?: ReactNode; bubble?: "left" | "down"; style?: CSSProperties }) {
  return (
    <div className={"tk-buddy" + (float ? " tk-buddy--float" : "")} style={{ width, maxWidth: "100%", ...style }}>
      <img src={A.chefBuddy} alt="Chef Buddy, the thatKorner concierge: a white rounded character in an orange chef's hat and jacket with the K mark on its chest" width={679} height={1000} />
      {children && <div className={`tk-bubble tk-bubble--${bubble}`}>{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ chrome */
const NAV: { label: string; path: string; hash?: string }[] = [
  { label: "Chef Buddy", path: "/chef-buddy" },
  { label: "Chef Brain", path: "/chef-brain" },
  { label: "Korner Ask", path: "/korner-ask" },
  { label: "Platform", path: "/platform" },
  { label: "Pricing", path: "/platform", hash: "pricing" },
  { label: "Trust", path: "/trust" },
  { label: "Affiliates", path: "/affiliate" },
];

export function Nav({ current }: { current: string }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="tk-nav">
      <div className="tk-nav__in">
        <a className="tk-nav__logo" href={href("/")} aria-label="thatKorner home"><img src={A.wordmarkBone} alt="thatKorner" width={720} height={114} /></a>
        <nav className="tk-nav__links" aria-label="Primary">
          {NAV.map((n) => (
            <a key={n.label} href={href(n.path, n.hash)} aria-current={!n.hash && n.path === current ? "page" : undefined}>{n.label}</a>
          ))}
        </nav>
        <div className="tk-nav__end">
          <a className="tk-nav__login" href={TK.loginUrl}>Log in</a>
          <Btn to={CONTACT} sm>Book a demo</Btn>
          <button className="tk-nav__burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)}><Icon name={open ? "close" : "menu"} /></button>
        </div>
      </div>
      <nav className="tk-nav__panel" data-open={open} aria-label="Mobile">
        {NAV.map((n) => <a key={n.label} href={href(n.path, n.hash)} onClick={() => setOpen(false)}>{n.label}</a>)}
        <a href={TK.loginUrl}>Log in</a>
        <a href={TK.trialUrl}>Start a 15-day trial</a>
      </nav>
    </header>
  );
}

const ROLES = ["Hotel or group F&B leadership", "General manager", "Executive or corporate chef", "Restaurant owner or operator", "Sommelier or restaurant manager", "IT, security or procurement", "Partner or press", "Other"];

export function ContactBand({ title, lede }: { title?: ReactNode; lede?: string }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error" | "unwired">("idle");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    if (!TK.formEndpoint) { setState("unwired"); return; } // never claim a send that did not happen
    setState("sending");
    try {
      const r = await fetch(TK.formEndpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      setState(r.ok ? "sent" : "error");
    } catch { setState("error"); }
  }
  return (
    <section className="tk-sec tk-sec--ink" id="contact">
      <div className="tk-wrap tk-contact">
        <div className="tk-stack tk-stack--lg">
          <div>
            <Eyebrow>Book a demo</Eyebrow>
            <h2 className="tk-h2">{title ?? <>See it on <em>your own menu.</em></>}</h2>
          </div>
          <p className="tk-lede">{lede ?? "Send us a menu and tell us what you run. We reply personally within one working day and show the platform on your dishes, not on a sample restaurant."}</p>
          <div className="tk-reach">
            <div><span>Email</span><a href={`mailto:${TK.email}`}>{TK.email}</a></div>
            <div><span>Telephone</span>{TK.phones.map((p) => <a key={p} href={`tel:${p.replace(/\s/g, "")}`}>{p}</a>)}</div>
            <div><span>Restaurants with up to ten branches</span><a href={TK.trialUrl}>Start a 15-day trial, no card</a></div>
          </div>
        </div>
        {state === "sent" ? (
          <div className="tk-form" role="status" style={{ gridTemplateColumns: "minmax(0,1fr)", justifyItems: "start" }}>
            <Buddy width={120} />
            <h3 className="tk-h3">Thank you. Your enquiry is with us.</h3>
            <p className="tk-body">We have sent a confirmation email to your inbox. You can also register your restaurant directly on thatkorner.online.</p>
            <div style={{ marginTop: "1rem" }}>
              <Btn to={TK.trialUrl} kind="gold">Register Your Restaurant on thatkorner.online</Btn>
            </div>
          </div>
        ) : (
          <form className="tk-form" onSubmit={submit}>
            <label>Name<input id="tk-f-name" name="name" required autoComplete="name" /></label>
            <label>Work email<input id="tk-f-email" name="email" type="email" required autoComplete="email" /></label>
            <label>Property or company<input id="tk-f-company" name="company" required autoComplete="organization" /></label>
            <label>Telephone<input id="tk-f-phone" name="phone" type="tel" autoComplete="tel" /></label>
            <label className="full">Your role
              <select id="tk-f-role" name="role" required defaultValue="">
                <option value="" disabled>Choose one</option>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </label>
            <label className="full">What would you like to see?<textarea id="tk-f-msg" name="message" placeholder="Number of outlets, your current POS, and what you want the menu to do better." /></label>
            <div className="full tk-row">
              <button className="tk-btn" type="submit" disabled={state === "sending"}>{state === "sending" ? "Sending" : "Send enquiry"}<Icon name="arrow" /></button>
              {state === "error" && <small role="alert">That did not send. Please email {TK.email} and we will pick it up from there.</small>}
              {state === "unwired" && <small role="alert">This form is not connected yet, so nothing was sent. Please email {TK.email}.</small>}
            </div>
            <small className="full">We use these details only to reply to you. See our <a href={TK.legal.privacy}>privacy notice</a>.</small>
          </form>
        )}
      </div>
    </section>
  );
}

export function Closing() {
  return (
    <section className="tk-sec tk-sec--red tk-sec--tight">
      <div className="tk-wrap tk-close">
        <h2 className="tk-h2">Life happens <em>in the corner.</em></h2>
        <p style={{ maxWidth: "52ch" }}>The corner table is the best seat in the house and the furthest from service. thatKorner was built so that no guest sitting there is ever forgotten, and no dish on the menu goes unexamined.</p>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="tk-foot">
      <div className="tk-foot__grid">
        <div>
          <img src={A.wordmarkBone} alt="thatKorner" width={720} height={114} loading="lazy" />
          <p style={{ maxWidth: "38ch" }}>Culinary intelligence and restaurant operations for hotels, restaurants and hospitality groups.</p>
          <p className="tk-social__label">Follow thatKorner</p>
          <div className="tk-social">
            {TK.social.map((s) => (
              <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`thatKorner on ${s.label}`} title={s.label}>
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={s.d} /></svg>
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4>Products</h4>
          <ul>
            <li><a href={href("/chef-buddy")}>Chef Buddy, for guests</a></li>
            <li><a href={href("/chef-brain")}>Chef Brain, for operators</a></li>
            <li><a href={href("/korner-ask")}>Korner Ask, for the floor</a></li>
            <li><a href={href("/platform")}>The platform</a></li>
            <li><a href={href("/platform", "pricing")}>Pricing</a></li>
          </ul>
        </div>
        <div>
          <h4>Company</h4>
          <ul>
            <li><a href={href("/trust")}>Trust and security</a></li>
            <li><a href={href("/affiliate")}>Affiliate programme</a></li>
            <li><a href={CONTACT}>Book a demo</a></li>
            <li><a href={TK.trialUrl}>Start a 15-day trial</a></li>
            <li><a href={TK.loginUrl}>Log in</a></li>
          </ul>
        </div>
        <div>
          <h4>Contact</h4>
          <ul>
            <li><a href={`mailto:${TK.email}`}>{TK.email}</a></li>
            {TK.phones.map((p) => <li key={p}><a href={`tel:${p.replace(/\s/g, "")}`}>{p}</a></li>)}
            <li>Dubai, United Arab Emirates</li>
          </ul>
        </div>
      </div>
      <div className="tk-foot__legal">
        <p>thatKorner, Chef Buddy, Chef Brain and Korner Ask are trademarks of {TK.legalEntity}. Patent pending.</p>
        <div>
          <span>© 2026 {TK.legalEntity}. {TK.address}</span>
          <span><a href={TK.legal.privacy}>Privacy</a> · <a href={TK.legal.terms}>Terms</a> · <a href={TK.legal.cookies}>Cookies</a> · <a href={TK.legal.dpa}>Data processing agreement</a></span>
        </div>
      </div>
    </footer>
  );
}

function Consent() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (PREVIEW) return; // the standalone preview stores nothing, so it does not ask
    try { if (!document.cookie.includes("tk_consent=")) setShow(true); } catch { /* storage unavailable: stay hidden */ }
  }, []);
  function choose(granted: boolean) {
    try { document.cookie = `tk_consent=${granted ? "all" : "essential"}; max-age=15552000; path=/; SameSite=Lax`; } catch { /* ignore */ }
    TK.onConsent(granted);
    setShow(false);
  }
  if (!show) return null;
  return (
    <div className="tk-consent" role="dialog" aria-label="Cookies">
      <p>We use essential cookies to run this site and, with your consent, analytics cookies to improve it. <a href={TK.legal.cookies}>Cookie policy</a></p>
      <div className="tk-row">
        <button className="tk-btn tk-btn--ghost tk-btn--sm" onClick={() => choose(false)}>Essential only</button>
        <button className="tk-btn tk-btn--ink tk-btn--sm" onClick={() => choose(true)}>Accept analytics</button>
      </div>
    </div>
  );
}

export function Page({ current, children, contact, tone }: { current: string; children: ReactNode; contact?: { title?: ReactNode; lede?: string }; tone?: "luxe" }) {
  return (
    <div className={"tk" + (tone ? ` tk--${tone}` : "")}>
      <Nav current={current} />
      <main>
        {children}
        <ContactBand {...contact} />
        <Closing />
      </main>
      <Footer />
      <Consent />
    </div>
  );
}
